import React from "react";
import { FormattedMessage } from "react-intl";
import { useTheme } from "styled-components";
import { criteriaModalMessages as messages } from "../../../orchestrator.messages";
import { OccupationOption } from "../../hooks";
import { OccupancyTypeFilters } from "../../hooks/useCriteriaForm";

interface OccupancyInputProps {
  occupations: OccupationOption[];
  selectedOccupations: string[];
  setSelectedOccupations: React.Dispatch<React.SetStateAction<string[]>>;
  occupancyTypeFilters: OccupancyTypeFilters;
  setOccupancyTypeFilters: React.Dispatch<React.SetStateAction<OccupancyTypeFilters>>;
}

type OccupancyTypeKey = keyof OccupancyTypeFilters;

export function OccupancyInput({
  occupations,
  selectedOccupations,
  setSelectedOccupations,
  occupancyTypeFilters,
  setOccupancyTypeFilters,
}: OccupancyInputProps) {
  const theme = useTheme();

  const filteredOccupations = occupations.filter(occ => {
    if (occupancyTypeFilters.adults && occ.adults === 0) return false;
    if (occupancyTypeFilters.children && occ.children === 0) return false;
    if (occupancyTypeFilters.juniors && occ.juniors === 0) return false;
    if (occupancyTypeFilters.seniors && occ.seniors === 0) return false;
    return !(occupancyTypeFilters.babies && occ.babies === 0);
  });

  const handleSelectAll = () => {
    const filteredTickers = filteredOccupations.map(occ => occ.ticker);
    setSelectedOccupations(prev => {
      const newSelection = new Set(prev);
      filteredTickers.forEach(t => newSelection.add(t));
      return Array.from(newSelection);
    });
  };

  const handleDeselectAll = () => {
    const filteredTickers = new Set(filteredOccupations.map(occ => occ.ticker));
    setSelectedOccupations(prev => prev.filter(t => !filteredTickers.has(t)));
  };

  const typeFilters: Array<{ key: OccupancyTypeKey; visible: boolean }> = [
    { key: "adults", visible: occupations.some(occ => occ.adults > 0) },
    { key: "children", visible: occupations.some(occ => occ.children > 0) },
    { key: "juniors", visible: occupations.some(occ => occ.juniors > 0) },
    { key: "seniors", visible: occupations.some(occ => occ.seniors > 0) },
    { key: "babies", visible: occupations.some(occ => occ.babies > 0) },
  ];

  return (
    <div>
      <label className="block font-medium text-gray-700 mb-2">
        <FormattedMessage {...messages.values} />
      </label>

      {/* Filter by type checkboxes */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
          <FormattedMessage {...messages.occupancyFilterLabel} />
        </label>
        <div className="flex flex-wrap gap-3">
          {typeFilters
            .filter(f => f.visible)
            .map(({ key }) => (
              <label
                key={key}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded border cursor-pointer text-sm select-none ${
                  occupancyTypeFilters[key]
                    ? `bg-[${theme.textColor}] text-white border-[${theme.textColor}]`
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}>
                <input
                  type="checkbox"
                  checked={occupancyTypeFilters[key]}
                  onChange={e =>
                    setOccupancyTypeFilters(prev => ({ ...prev, [key]: e.target.checked }))
                  }
                  className="sr-only"
                />
                <FormattedMessage {...messages[key]} />
              </label>
            ))}
        </div>
      </div>

      {/* Selection buttons */}
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={handleSelectAll}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <FormattedMessage {...messages.selectAll} />
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={handleDeselectAll}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <FormattedMessage {...messages.deselectAll} />
        </button>
        <span className="text-gray-400 text-sm ml-auto">
          <FormattedMessage
            {...messages.selectedCount}
            values={{ count: selectedOccupations.length }}
          />
        </span>
      </div>

      {/* Occupancy list */}
      <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
        {filteredOccupations.map(occ => (
          <label
            key={occ.ticker}
            className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded cursor-pointer">
            <input
              type="checkbox"
              checked={selectedOccupations.includes(occ.ticker)}
              onChange={() => {
                setSelectedOccupations(prev =>
                  prev.includes(occ.ticker)
                    ? prev.filter(t => t !== occ.ticker)
                    : [...prev, occ.ticker]
                );
              }}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">
              {occ.name} ({occ.ticker})
            </span>
          </label>
        ))}
        {filteredOccupations.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-2">
            <FormattedMessage {...messages.noMatchingOccupations} />
          </div>
        )}
      </div>
    </div>
  );
}
